import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaymentOrderSummary from "../components/payment/PaymentOrderSummary";
import PaymentSuccessView from "../components/payment/PaymentSuccessView";
import StripeSubscriptionCheckout from "../components/payment/StripeSubscriptionCheckout";
import { MaterialIcon } from "../components/payment/checkoutUi";
import {
  useTranslation,
  resolveCheckoutFromState,
  formatEuro,
} from "../../../i18n";
import {
  fetchSubscriptionCheckoutStatus,
  fetchPaymentHistory,
} from "../subscriptionApi";
import api from "../../../utils/api";
import { persistAuthUser } from "../../../lib/authSession";
import { applyAccountStatusToSession } from "../../../lib/accountStatus";
import useClientAccountStatus from "../hooks/useClientAccountStatus";

function formatHistoryDate(iso, locale) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ClientPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, locale } = useTranslation();
  const { isPaid, refresh: refreshAccountStatus } = useClientAccountStatus();
  const checkout = useMemo(
    () => resolveCheckoutFromState(location.state, t),
    [location.state, t]
  );

  const [view, setView] = useState("checkout");
  const [activeTab, setActiveTab] = useState("pay");
  const [history, setHistory] = useState([]);

  // Real invoice history from Stripe (via the backend).
  const loadHistory = async () => {
    try {
      setHistory(await fetchPaymentHistory());
    } catch {
      setHistory([]);
    }
  };
  const [returnMessage, setReturnMessage] = useState("");
  const [verifyingReturn, setVerifyingReturn] = useState(false);

  const totalLabel = formatEuro(checkout.total);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    const sessionId = searchParams.get("session_id");

    if (checkoutResult === "cancelled") {
      setReturnMessage(t("payment.checkoutCancelled"));
      setSearchParams({}, { replace: true });
      return;
    }

    if (checkoutResult !== "success" || !sessionId) {
      return;
    }

    let cancelled = false;
    setVerifyingReturn(true);

    (async () => {
      try {
        const result = await fetchSubscriptionCheckoutStatus(sessionId);
        if (cancelled) return;

        if (!result?.paid) {
          setReturnMessage(t("payment.verifyPaymentFailed"));
          return;
        }

        try {
          const profile = await api.get("/api/users/profile/", { auth: true });
          persistAuthUser(profile);
          applyAccountStatusToSession(profile);
        } catch {
          /* subscription activated via webhook or verify endpoint */
        }
        await refreshAccountStatus();

        // Pull the authoritative invoice list from Stripe (the payment just
        // completed, so the new invoice is included).
        await loadHistory();
        setView("success");
      } catch {
        if (!cancelled) setReturnMessage(t("payment.verifyPaymentFailed"));
      } finally {
        if (!cancelled) {
          setVerifyingReturn(false);
          setSearchParams({}, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    checkout.title,
    checkout.total,
    refreshAccountStatus,
    searchParams,
    setSearchParams,
    t,
  ]);

  if (view === "success") {
    return (
      <PaymentSuccessView
        onComplete={() => navigate("/dashboard/client", { replace: true })}
      />
    );
  }

  return (
    <main className="min-h-screen bg-background font-body-md text-on-surface">
      <div className="mx-auto max-w-[1280px] px-margin-desktop py-stack-lg">
        <header className="mb-stack-lg">
          <Link
            to="/dashboard/client"
            className="mb-4 inline-flex items-center gap-2 font-body-md text-on-surface-variant transition-colors hover:text-primary"
          >
            <MaterialIcon name="arrow_back" className="text-[18px]" />
            {t("payment.backToDashboard")}
          </Link>

          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-headline-xl text-headline-xl text-on-surface">
                {t("payment.secureCheckout")}
              </h1>
              <p className="mt-1 font-body-lg text-on-surface-variant">
                {t("payment.checkoutSubtitle")}
              </p>
            </div>

            <div className="flex rounded-lg bg-surface-container p-1">
              <button
                type="button"
                onClick={() => setActiveTab("pay")}
                className={[
                  "rounded-md px-6 py-2 font-label-sm transition-colors",
                  activeTab === "pay"
                    ? "bg-white text-on-surface shadow-sm"
                    : "text-on-surface-variant hover:text-on-surface",
                ].join(" ")}
              >
                {t("payment.checkout")}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("history")}
                className={[
                  "rounded-md px-6 py-2 font-label-sm transition-colors",
                  activeTab === "history"
                    ? "bg-white text-on-surface shadow-sm"
                    : "text-on-surface-variant hover:text-on-surface",
                ].join(" ")}
              >
                {t("payment.paymentHistory")}
              </button>
            </div>
          </div>
        </header>

        {returnMessage ? (
          <div className="mb-6 rounded-lg bg-surface-container px-4 py-3 text-body-md text-on-surface-variant">
            {returnMessage}
          </div>
        ) : null}

        {verifyingReturn ? (
          <div className="mb-6 flex items-center gap-3 rounded-lg bg-surface-container px-4 py-3 text-body-md text-on-surface-variant">
            <MaterialIcon name="refresh" className="animate-spin text-[20px]" />
            {t("payment.verifyingPayment")}
          </div>
        ) : null}

        {activeTab === "history" ? (
          <section className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest shadow-soft">
            {history.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <MaterialIcon
                  name="receipt_long"
                  className="mx-auto mb-4 text-[48px] text-outline-variant"
                />
                <p className="font-headline-md text-headline-md text-on-surface">
                  {t("payment.noPayments")}
                </p>
                <p className="mt-2 font-body-md text-on-surface-variant">
                  {t("payment.completedPayments")}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("pay")}
                  className="mt-6 font-label-sm font-semibold text-secondary hover:underline"
                >
                  {t("payment.goToCheckout")}
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-outline-variant/30">
                {history.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-3 px-8 py-5"
                  >
                    <div>
                      <p className="font-body-lg font-semibold text-on-surface">
                        {row.title}
                      </p>
                      <p className="mt-1 text-label-sm text-on-surface-variant">
                        {formatHistoryDate(row.createdAt, locale)} · Stripe · {row.reference}
                      </p>
                    </div>
                    <span className="font-headline-md text-headline-md text-secondary">
                      {formatEuro(row.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <div className="grid grid-cols-1 gap-gutter lg:grid-cols-12">
            <div className="lg:col-span-5">
              <PaymentOrderSummary checkout={checkout} />
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-stack-lg shadow-soft">
                <h2 className="mb-2 font-headline-lg text-headline-lg text-on-surface">
                  {t("payment.stripeCheckoutTitle")}
                </h2>
                <p className="mb-8 font-body-md text-on-surface-variant">
                  {t("payment.stripeRedirectNote")}
                </p>

                <StripeSubscriptionCheckout
                  totalLabel={totalLabel}
                  plan={checkout.plan || "premium"}
                  isAlreadySubscribed={isPaid}
                />
              </div>

              <div className="mt-8 flex flex-col items-center justify-between gap-4 font-label-sm text-on-surface-variant md:flex-row">
                <p>{t("payment.footerCopyright")}</p>
                <div className="flex gap-6">
                  <Link
                    to="/dashboard/client/documentation"
                    className="transition-colors hover:text-primary"
                  >
                    {t("payment.privacyPolicy")}
                  </Link>
                  <Link
                    to="/dashboard/client/documentation"
                    className="transition-colors hover:text-primary"
                  >
                    {t("payment.termsOfService")}
                  </Link>
                  <Link
                    to="/dashboard/client/support"
                    className="transition-colors hover:text-primary"
                  >
                    {t("common.support")}
                  </Link>
                </div>
              </div>

              <p className="mt-6 text-center text-label-sm text-on-surface-variant">
                {t("payment.subscriptionOnlyNote")}
              </p>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ClientPaymentPage;
